import React, { useState, useEffect } from 'react';
import {
    Box, Text, Badge, Accordion, AccordionItem, AccordionButton,
    AccordionPanel, AccordionIcon, Button, Menu, MenuButton, MenuList, MenuItem, Icon,
    VStack, HStack, Wrap, SimpleGrid, Heading, useConst, Tooltip, MenuGroup,
    Divider,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import ProdutoImagem from "../ProdutoImagem";
import { MdAttachment, MdInfoOutline, MdFolderOpen } from 'react-icons/md';
import DevolucoesFileUpload from "../DevolucoesFileUpload";
import {
    formatBRL, formatDeadlineShort,
    MONEY_OWNER_META, URGENCY_META,
    getStatusDevolucaoLabel, getStatusDevolucaoColor,
    getStatusMoneyLabel, getStatusMoneyColor,
} from '../../utils/formatters';
import {
    FaClock, FaArrowDown, FaLock, FaHandHoldingUsd, FaQuestionCircle,
    FaPaperclip, FaBoxOpen, FaIdCard, FaRobot, FaTag, FaUser,
    FaExclamationCircle, FaFlag, FaCheckCircle, FaTimesCircle, FaLayerGroup,
} from 'react-icons/fa';

const MONEY_ICONS = {
    buyer:   FaArrowDown,
    ml:      FaLock,
    seller:  FaHandHoldingUsd,
    unknown: FaQuestionCircle,
};

const FORNECEDORES = { 1: "MORELATE", 2: "VANNUCCI", 3: "ISAPA", 4: "BRCOMPANY", 5: "AUTOLATINA", 6: "LNG", 7: "CBA", 8: "MSAM", 9: "BLPARTS" };

const formatarDataBr = (isoStr) => {
    if (!isoStr) return "N/A";
    const date = new Date(isoStr);
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const formatarDoc = (doc, tipo) => {
    if (!doc) return "N/A";
    if (tipo === "CNPJ") return doc.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    return doc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
};

const InfoRow = ({ label, children, isBold = true }) => (
    <Box>
        <Text fontSize="10px" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">{label}</Text>
        <Text fontWeight={isBold ? "semibold" : "normal"} fontSize="sm" wordBreak="break-word" color="gray.800">
            {children || 'N/A'}
        </Text>
    </Box>
);

const IdChip = ({ icon, label, value, color = 'gray' }) => (
    <HStack spacing={1.5} color={`${color}.600`}>
        <Icon as={icon} boxSize={3} />
        <Text fontSize="11px">
            {label} <Text as="span" fontWeight="bold" color={`${color}.800`} fontFamily="mono">{value || '—'}</Text>
        </Text>
    </HStack>
);

const IconBadge = ({ icon, children, colorScheme, variant = 'subtle', opacity = 1 }) => (
    <Badge
        colorScheme={colorScheme}
        variant={variant}
        opacity={opacity}
        display="inline-flex"
        alignItems="center"
        gap="5px"
        px={2}
        py={0.5}
        borderRadius="md"
        fontSize="11px"
        fontWeight="semibold"
    >
        {icon && <Icon as={icon} boxSize={2.5} />}
        {children}
    </Badge>
);

const DevolucaoCard = ({ devolucao, onUpdate }) => {
    const [localData, setLocalData] = useState(devolucao);
    useEffect(() => { setLocalData(devolucao); }, [devolucao]);

    const handleUpdate = (key, value) => {
        const updated = { ...localData, [key]: value };
        setLocalData(updated);
        onUpdate(localData.id, key, value);
    };

    const {
        id, id_ml, id_order_ml, id_venda, pack_id_ml, conta, date_closed, estimated_delivery_time,
        reviewed, received, date_created, claim_id, status_money,
        type, subtype, status, seller_review, orders,
        dados_cliente, dados_adicionais, avaria,
    } = localData || {};

    const itemPrincipal = orders?.[0] || {};
    const adicional = dados_adicionais || {};
    const cliente = dados_cliente || {};

    const motivo = adicional.problem || adicional.description || '';
    const resolucao = localData?.review_title_ml || adicional.title || '';
    const mostrarResolucao = resolucao && resolucao !== motivo;

    const urgency = localData?.urgency_level || 'sem_prazo';
    const urgencyMeta = URGENCY_META[urgency] || URGENCY_META.sem_prazo;
    const isUrgent = urgency === 'urgente' || urgency === 'atrasada';
    const deadlineText = formatDeadlineShort(localData?.review_due_date_util, '');
    const deadlineLabel = urgency === 'urgente'
        ? 'Revisar HOJE'
        : urgency === 'atrasada'
            ? `Atrasada${deadlineText ? ` (${deadlineText})` : ''}`
            : deadlineText ? `Revisar ${deadlineText}` : urgencyMeta.label;

    const precoTotal = Number(itemPrincipal.preco_total || 0);

    const moneyOwner = localData?.money_owner;
    const moneyMeta = MONEY_OWNER_META[moneyOwner];
    const MoneyIcon = MONEY_ICONS[moneyOwner] || FaQuestionCircle;
    const moneyShortMap = { buyer: 'Devolvido', ml: 'Retido ML', seller: 'Recuperado', unknown: 'Indefinido' };
    const moneyLabel = moneyOwner === 'buyer' && Number(localData?.total_perdido) > 0
        ? `${moneyShortMap.buyer} ${formatBRL(localData.total_perdido)}`
        : moneyShortMap[moneyOwner] || null;

    const anexosCount = Array.isArray(localData?.arquivos_anexados) ? localData.arquivos_anexados.length : 0;
    const temIa = !!(localData?.pre_analise && localData.pre_analise.includes('ANÁLISE AUTOMÁTICA DE DEFEITOS'));

    const cliente_isPJ = cliente.doc_type === "CNPJ";
    const cliente_nome = cliente_isPJ
        ? cliente.business_name
        : `${cliente.first_name ?? ""} ${cliente.last_name ?? ""}`.trim();

    const reviewRoot = seller_review?.reviews?.[0];
    const reviewDetail = reviewRoot?.resource_reviews?.[0];

    const urgencyBarColor = isUrgent ? 'red.500' : urgency === 'proxima' ? 'orange.400' : null;

    return (
        <Box
            bg="white"
            borderWidth="1px"
            borderColor={isUrgent ? "red.300" : "gray.200"}
            borderRadius="xl"
            overflow="hidden"
            boxShadow="sm"
            mb={4}
            transition="all 0.15s"
            _hover={{ boxShadow: 'md', borderColor: isUrgent ? 'red.400' : 'gray.300' }}
        >
            {urgencyBarColor && (
                <Box h="3px" bg={urgencyBarColor} />
            )}

            <Box p={{ base: 3, md: 4 }}>
                <HStack align="stretch" spacing={{ base: 3, md: 4 }}>
                    <Box
                        w={{ base: "70px", md: "100px" }}
                        h={{ base: "70px", md: "100px" }}
                        bg="gray.50"
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor="gray.200"
                        overflow="hidden"
                        flexShrink={0}
                    >
                        <ProdutoImagem
                            produto={itemPrincipal}
                            origem={null}
                            width={{ base: "70px", md: "100px" }}
                            height={{ base: "70px", md: "100px" }}
                        />
                    </Box>

                    <VStack align="stretch" spacing={1.5} flex={1} minW={0}>
                        <HStack justify="space-between" align="flex-start" spacing={2}>
                            <Heading size="sm" noOfLines={2} title={itemPrincipal.nome} color="gray.800" lineHeight="1.3">
                                {itemPrincipal.nome || "Produto não identificado"}
                            </Heading>
                            {precoTotal > 0 && (
                                <VStack spacing={0} align="flex-end" flexShrink={0}>
                                    <Text fontSize="10px" color="gray.500" fontWeight="bold" textTransform="uppercase">Valor</Text>
                                    <Text fontSize="lg" fontWeight="extrabold" color="gray.800" lineHeight="1">
                                        {formatBRL(precoTotal)}
                                    </Text>
                                </VStack>
                            )}
                        </HStack>

                        {motivo && (
                            <HStack spacing={1.5} color="red.600" align="flex-start">
                                <Icon as={FaExclamationCircle} boxSize={3} mt={0.5} flexShrink={0} />
                                <Text fontSize="sm" fontStyle="italic" noOfLines={2} lineHeight="1.35">
                                    {motivo}
                                </Text>
                            </HStack>
                        )}
                        {mostrarResolucao && (
                            <HStack spacing={1.5} color="gray.500" align="flex-start">
                                <Icon as={FaFlag} boxSize={2.5} mt={0.5} flexShrink={0} />
                                <Text fontSize="xs" noOfLines={1} lineHeight="1.35">
                                    {resolucao}
                                </Text>
                            </HStack>
                        )}

                        <HStack spacing={3} pt={1} wrap="wrap" rowGap={1}>
                            {cliente_nome && (
                                <HStack spacing={1} color="gray.600">
                                    <Icon as={FaUser} boxSize={2.5} />
                                    <Text fontSize="xs" noOfLines={1}>{cliente_nome}</Text>
                                </HStack>
                            )}
                            {itemPrincipal.sku && (
                                <HStack spacing={1} color="gray.600">
                                    <Icon as={FaTag} boxSize={2.5} />
                                    <Text fontSize="xs">SKU <Text as="span" fontWeight="semibold">{itemPrincipal.sku}</Text></Text>
                                </HStack>
                            )}
                            {itemPrincipal.codigo_global && (
                                <Text fontSize="xs" color="gray.600">
                                    Cód <Text as="span" fontWeight="semibold" fontFamily="mono">{itemPrincipal.codigo_global}</Text>
                                </Text>
                            )}
                        </HStack>

                        <Wrap spacingX={3} spacingY={1} pt={0.5}>
                            <IdChip icon={FaBoxOpen} label="Venda" value={`#${id_order_ml}`} color="gray" />
                            {pack_id_ml && pack_id_ml !== id_order_ml && (
                                <IdChip icon={FaLayerGroup} label="Pack" value={`#${pack_id_ml}`} color="gray" />
                            )}
                            <IdChip icon={FaIdCard} label="Dev" value={`#${id_ml}`} color="gray" />
                            <Badge colorScheme={conta == 1 ? 'orange' : String(conta) === '3' ? 'green' : 'blue'} variant="subtle" fontSize="10px" fontWeight="bold">
                                CONTA {conta}
                            </Badge>
                        </Wrap>
                    </VStack>

                    <VStack align="flex-end" spacing={2} flexShrink={0} display={{ base: 'none', md: 'flex' }}>
                        <Menu>
                            <MenuButton
                                as={Button}
                                rightIcon={<ChevronDownIcon />}
                                colorScheme="orange"
                                size="sm"
                                variant="solid"
                            >
                                Ações
                            </MenuButton>
                            <MenuList>
                                <MenuGroup title="Status do Recebimento">
                                    <MenuItem onClick={() => handleUpdate('received', true)}>Marcar como RECEBIDA</MenuItem>
                                    <MenuItem onClick={() => handleUpdate('received', false)}>Marcar como NÃO RECEBIDA</MenuItem>
                                </MenuGroup>
                                <MenuGroup title="Status do Item">
                                    <MenuItem onClick={() => handleUpdate('avaria', true)}>Marcar como AVARIA</MenuItem>
                                    <MenuItem onClick={() => handleUpdate('avaria', false)}>Marcar como SEM DEFEITO</MenuItem>
                                </MenuGroup>
                            </MenuList>
                        </Menu>
                    </VStack>
                </HStack>

                <Divider my={3} />

                <Wrap spacing={2} rowGap={2}>
                    {urgency !== 'sem_prazo' && (
                        <Tooltip label={urgencyMeta.description} hasArrow>
                            <IconBadge
                                icon={FaClock}
                                colorScheme={urgencyMeta.color}
                                variant={isUrgent ? 'solid' : 'subtle'}
                            >
                                {deadlineLabel}
                            </IconBadge>
                        </Tooltip>
                    )}
                    <IconBadge colorScheme={getStatusDevolucaoColor(status)} variant="subtle">
                        {getStatusDevolucaoLabel(status)}
                    </IconBadge>
                    {status_money && (
                        <IconBadge colorScheme={getStatusMoneyColor(status_money)} variant="subtle">
                            {getStatusMoneyLabel(status_money)}
                        </IconBadge>
                    )}
                    {moneyLabel && moneyMeta && (
                        <Tooltip label={moneyMeta.description} hasArrow>
                            <IconBadge icon={MoneyIcon} colorScheme={moneyMeta.color} variant="subtle">
                                {moneyLabel}
                            </IconBadge>
                        </Tooltip>
                    )}
                    <Tooltip label={anexosCount > 0 ? `${anexosCount} arquivo(s) anexado(s)` : 'Sem imagens anexadas'} hasArrow>
                        <IconBadge
                            icon={FaPaperclip}
                            colorScheme={anexosCount > 0 ? 'blue' : 'gray'}
                            variant={anexosCount > 0 ? 'solid' : 'outline'}
                            opacity={anexosCount > 0 ? 1 : 0.7}
                        >
                            {anexosCount} {anexosCount === 1 ? 'anexo' : 'anexos'}
                        </IconBadge>
                    </Tooltip>
                    <Tooltip label={temIa ? 'Análise de IA disponível' : 'IA ainda não analisou'} hasArrow>
                        <IconBadge
                            icon={FaRobot}
                            colorScheme={temIa ? 'teal' : 'gray'}
                            variant={temIa ? 'solid' : 'outline'}
                            opacity={temIa ? 1 : 0.7}
                        >
                            IA {temIa ? '✓' : '—'}
                        </IconBadge>
                    </Tooltip>

                    <Box flex={1} />

                    <IconBadge
                        icon={received ? FaCheckCircle : FaTimesCircle}
                        colorScheme={received ? 'green' : 'gray'}
                        variant={received ? 'solid' : 'outline'}
                    >
                        {received ? 'Recebida' : 'Não recebida'}
                    </IconBadge>
                    <IconBadge
                        icon={avaria ? FaTimesCircle : FaCheckCircle}
                        colorScheme={avaria ? 'red' : 'green'}
                        variant="subtle"
                    >
                        {avaria ? 'Avaria' : 'Sem defeito'}
                    </IconBadge>
                    <IconBadge
                        icon={reviewed ? FaCheckCircle : FaClock}
                        colorScheme={reviewed ? 'green' : 'red'}
                        variant={reviewed ? 'subtle' : 'solid'}
                    >
                        {reviewed ? 'Revisada' : 'Não revisada'}
                    </IconBadge>
                </Wrap>

                <Menu>
                    <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        colorScheme="orange"
                        size="sm"
                        w="100%"
                        mt={3}
                        display={{ base: 'flex', md: 'none' }}
                    >
                        Ações
                    </MenuButton>
                    <MenuList>
                        <MenuGroup title="Status do Recebimento">
                            <MenuItem onClick={() => handleUpdate('received', true)}>Marcar como RECEBIDA</MenuItem>
                            <MenuItem onClick={() => handleUpdate('received', false)}>Marcar como NÃO RECEBIDA</MenuItem>
                        </MenuGroup>
                        <MenuGroup title="Status do Item">
                            <MenuItem onClick={() => handleUpdate('avaria', true)}>Marcar como AVARIA</MenuItem>
                            <MenuItem onClick={() => handleUpdate('avaria', false)}>Marcar como SEM DEFEITO</MenuItem>
                        </MenuGroup>
                    </MenuList>
                </Menu>
            </Box>

            <Accordion allowToggle borderTopWidth="1px" borderColor="gray.100" bg="gray.50">
                <AccordionItem borderColor="gray.200">
                    <AccordionButton _hover={{ bg: 'gray.100' }}>
                        <Box flex="1" textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.700">
                            <Icon as={MdInfoOutline} mr={2} verticalAlign="middle" color="orange.500" />
                            Detalhes do item e cliente
                        </Box>
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4} bg="white">
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                            <VStack align="stretch" spacing={3}>
                                <Heading size="xs" color="orange.600" letterSpacing="wider" textTransform="uppercase">
                                    Item devolvido
                                </Heading>
                                <SimpleGrid columns={2} spacing={3}>
                                    <InfoRow label="SKU">{itemPrincipal.sku}</InfoRow>
                                    <InfoRow label="Cód. Global">{itemPrincipal.codigo_global}</InfoRow>
                                    <InfoRow label="Preço Unit.">{formatBRL(itemPrincipal.preco)}</InfoRow>
                                    <InfoRow label="Qtd. Devolvida">{itemPrincipal.return_quantity}</InfoRow>
                                    <InfoRow label="Fornecedor">{FORNECEDORES[itemPrincipal.fornecedor_id] || 'N/I'}</InfoRow>
                                </SimpleGrid>
                            </VStack>
                            <VStack align="stretch" spacing={3}>
                                <Heading size="xs" color="orange.600" letterSpacing="wider" textTransform="uppercase">
                                    Cliente
                                </Heading>
                                <InfoRow label={cliente_isPJ ? "Razão Social" : "Nome"}>{cliente_nome}</InfoRow>
                                <InfoRow label={cliente.doc_type || 'Documento'}>{formatarDoc(cliente.doc_number, cliente.doc_type)}</InfoRow>
                                <InfoRow label="Endereço">
                                    {[
                                        cliente.street_name,
                                        cliente.street_number,
                                        cliente.neighborhood,
                                        cliente.city_name && `${cliente.city_name} - ${cliente.state_code || ''}`,
                                        cliente.zip_code && `CEP ${cliente.zip_code}`,
                                    ].filter(Boolean).join(', ')}
                                </InfoRow>
                            </VStack>
                        </SimpleGrid>
                    </AccordionPanel>
                </AccordionItem>

                <AccordionItem borderColor="gray.200">
                    <AccordionButton _hover={{ bg: 'gray.100' }}>
                        <Box flex="1" textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.700">
                            <Icon as={MdAttachment} mr={2} verticalAlign="middle" color="orange.500" />
                            Anexar imagens e análise
                            {anexosCount > 0 && (
                                <Badge ml={2} colorScheme="blue" variant="subtle" fontSize="10px">
                                    {anexosCount}
                                </Badge>
                            )}
                        </Box>
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4} bg="white">
                        <DevolucoesFileUpload
                            devolucaoId={id}
                            existingFiles={localData.arquivos_anexados || []}
                            preAnaliseExistente={localData.pre_analise || ''}
                            onUploadSuccess={(result) => {
                                const updatedData = {
                                    ...localData,
                                    arquivos_anexados: result.arquivos_salvos || localData.arquivos_anexados,
                                    pre_analise: result.pre_analise || localData.pre_analise
                                };
                                setLocalData(updatedData);
                                if (onUpdate) onUpdate(id, 'uploadSuccess', updatedData);
                            }}
                        />
                    </AccordionPanel>
                </AccordionItem>

                <AccordionItem borderColor="gray.200">
                    <AccordionButton _hover={{ bg: 'gray.100' }}>
                        <Box flex="1" textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.700">
                            <Icon as={MdFolderOpen} mr={2} verticalAlign="middle" color="orange.500" />
                            Datas, revisão e IDs
                        </Box>
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4} bg="white">
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 6 }}>
                            <VStack align="stretch" spacing={3}>
                                <Heading size="xs" color="orange.600" letterSpacing="wider" textTransform="uppercase">Datas</Heading>
                                <InfoRow label="Criação">{formatarDataBr(date_created)}</InfoRow>
                                <InfoRow label="Prev. Entrega">{formatarDataBr(estimated_delivery_time)}</InfoRow>
                                <InfoRow label="Limite Revisão">{formatarDataBr(date_closed)}</InfoRow>
                            </VStack>
                            <VStack align="stretch" spacing={3}>
                                <Heading size="xs" color="orange.600" letterSpacing="wider" textTransform="uppercase">Revisão do vendedor</Heading>
                                <InfoRow label="Status">{reviewDetail?.seller_status || "—"}</InfoRow>
                                <InfoRow label="Motivo">{reviewDetail?.reason_id || "—"}</InfoRow>
                                <InfoRow label="Fase">{reviewDetail?.stage || "—"}</InfoRow>
                            </VStack>
                            <VStack align="stretch" spacing={3}>
                                <Heading size="xs" color="orange.600" letterSpacing="wider" textTransform="uppercase">Identificadores</Heading>
                                <InfoRow label="ID TAGA"><Text as="span" fontFamily="mono">#{id_venda}</Text></InfoRow>
                                <InfoRow label="ID Pack (ML)"><Text as="span" fontFamily="mono">{pack_id_ml || '—'}</Text></InfoRow>
                                <InfoRow label="ID Reclamação"><Text as="span" fontFamily="mono">{claim_id}</Text></InfoRow>
                                <InfoRow label="Tipo / Subtipo">{type}{subtype ? ` / ${subtype}` : ''}</InfoRow>
                            </VStack>
                        </SimpleGrid>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </Box>
    );
};

export default DevolucaoCard;
